<template>
  <section
    class="slack-integration-card"
    aria-labelledby="slack-integration-card-title"
    :aria-busy="loading ? 'true' : 'false'"
  >
    <header class="slack-integration-card__header">
      <div class="slack-integration-card__logo">
        <img :src="slackLogoSrc" alt="" class="slack-integration-card__logo-img" />
      </div>
      <div class="slack-integration-card__heading">
        <h3 id="slack-integration-card-title" class="slack-integration-card__title">Slack</h3>
        <p class="slack-integration-card__description">{{ description }}</p>
      </div>
      <span
        class="slack-integration-card__badge"
        :class="`slack-integration-card__badge--${status}`"
        data-test="slack-status-badge"
      >
        {{ statusLabel }}
      </span>
    </header>

    <ul v-if="status === 'not_connected'" class="slack-integration-card__benefits">
      <li v-for="benefit in benefits" :key="benefit" class="slack-integration-card__benefit">
        <svg
          class="slack-integration-card__check"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M16.667 5 7.5 14.167 3.333 10"
            stroke="#7f56d9"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span>{{ benefit }}</span>
      </li>
    </ul>

    <dl v-else-if="detailRows.length" class="slack-integration-card__details">
      <div v-for="row in detailRows" :key="row.label" class="slack-integration-card__detail">
        <dt class="slack-integration-card__detail-label">{{ row.label }}</dt>
        <dd class="slack-integration-card__detail-value">{{ row.value }}</dd>
      </div>
    </dl>

    <div
      v-if="status === 'needs_reconnect'"
      class="slack-integration-card__banner slack-integration-card__banner--error"
      role="alert"
      data-test="slack-reconnect-banner"
    >
      <img :src="alertIconSrc" alt="" class="slack-integration-card__banner-icon" />
      <p class="slack-integration-card__banner-text">{{ reconnectMessage }}</p>
    </div>

    <div
      v-if="status === 'not_connected' && canManage"
      class="slack-integration-card__banner slack-integration-card__banner--info"
      role="note"
      data-test="slack-approval-note"
    >
      <img :src="infoIconSrc" alt="" class="slack-integration-card__banner-icon" />
      <p class="slack-integration-card__banner-text">{{ approvalNote }}</p>
    </div>

    <div v-if="canManage" class="slack-integration-card__actions">
      <StandardButton
        v-if="status === 'not_connected'"
        type="magenta"
        variant="primary"
        size="medium"
        label="Connect Slack"
        :disabled="loading"
        :loader-left="loading"
        @click="emit('connect')"
      />
      <StandardButton
        v-else-if="status === 'connected'"
        type="red"
        variant="outline"
        size="medium"
        label="Disconnect"
        :disabled="loading"
        :loader-left="loading"
        @click="emit('disconnect')"
      />
      <StandardButton
        v-else
        type="magenta"
        variant="primary"
        size="medium"
        label="Reconnect"
        :disabled="loading"
        :loader-left="loading"
        @click="emit('reconnect')"
      />
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const slackLogoSrc = require("@components/assets/slackLogo.svg");
const infoIconSrc = require("@components/assets/info-circle-brand.svg");
const alertIconSrc = require("@components/assets/alert-circle-error.svg");

const STATUS_LABELS = {
  not_connected: "Not connected",
  connected: "Connected",
  needs_reconnect: "Needs reconnect",
};

const benefits = [
  "Connect once for the whole workspace. Employees never link or configure anything.",
  "Employees are matched by email address. Email always remains the fallback.",
];
const approvalNote =
  "If Slack asks you to request approval, a Slack workspace admin must approve exIQtive first. Come back and click Connect Slack again once approved.";
const reconnectMessage =
  "exIQtive lost access to your Slack workspace. Slack notifications are paused. Employees still receive email.";

const props = defineProps({
  /** Account-level Slack connection state from the integrations status API. */
  status: {
    type: String,
    default: "not_connected",
    validator: (value) => ["not_connected", "connected", "needs_reconnect"].includes(value),
  },
  /** Only the Account Owner / Co-Owner can connect, reconnect or disconnect. */
  canManage: {
    type: Boolean,
    default: false,
  },
  workspaceName: {
    type: String,
    default: "",
  },
  /** Who connected, as returned by the status API: { name, email }. */
  connectedBy: {
    type: Object,
    default: null,
  },
  /** ISO date/time the workspace was connected. */
  connectedAt: {
    type: String,
    default: "",
  },
  /** Disables the action while a connect / reconnect / disconnect request is in flight. */
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["connect", "reconnect", "disconnect"]);

const statusLabel = computed(() => STATUS_LABELS[props.status]);

const description = computed(() =>
  props.status === "connected"
    ? "Notifications are delivered as Slack direct messages."
    : "Send exIQtive notifications to your employees as Slack direct messages."
);

const formattedConnectedAt = computed(() => {
  if (!props.connectedAt) return "";
  const date = new Date(props.connectedAt);
  if (Number.isNaN(date.getTime())) return props.connectedAt;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
});

const connectedByName = computed(() => props.connectedBy?.name || "");

const lastConnected = computed(() =>
  [formattedConnectedAt.value, connectedByName.value && `by ${connectedByName.value}`]
    .filter(Boolean)
    .join(" ")
);

const detailRows = computed(() => {
  if (props.status === "connected") {
    return [
      { label: "Workspace", value: props.workspaceName },
      { label: "Connected by", value: connectedByName.value },
      { label: "Connected on", value: formattedConnectedAt.value },
      { label: "Delivery", value: "Direct messages, matched by email. Email fallback on." },
    ].filter((row) => row.value);
  }
  if (props.status === "needs_reconnect" && lastConnected.value) {
    return [{ label: "Last connected", value: lastConnected.value }];
  }
  return [];
});
</script>

<style scoped>
.slack-integration-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  padding: 24px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #fff;
  font-family: Inter, sans-serif;
}

.slack-integration-card__header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.slack-integration-card__logo {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  padding: 2px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #fff;
}

.slack-integration-card__logo-img {
  display: block;
  width: 100%;
  height: auto;
}

.slack-integration-card__heading {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.slack-integration-card__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.slack-integration-card__description {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.slack-integration-card__badge {
  flex-shrink: 0;
  padding: 2px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.slack-integration-card__badge--not_connected {
  background: #f9fafb;
  color: #344054;
}

.slack-integration-card__badge--connected {
  background: #ecfdf3;
  color: #027a48;
}

.slack-integration-card__badge--needs_reconnect {
  background: #fee4e2;
  color: #b42318;
}

.slack-integration-card__benefits {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.slack-integration-card__benefit {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #344054;
}

.slack-integration-card__check {
  flex-shrink: 0;
}

.slack-integration-card__details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
}

.slack-integration-card__detail {
  display: flex;
  gap: 16px;
  font-size: 14px;
  line-height: 20px;
}

.slack-integration-card__detail-label {
  flex-shrink: 0;
  width: 140px;
  font-weight: 500;
  color: #475467;
}

.slack-integration-card__detail-value {
  margin: 0;
  font-weight: 400;
  color: #344054;
}

.slack-integration-card__banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
}

.slack-integration-card__banner--info {
  background: #f4ebff;
}

.slack-integration-card__banner--info .slack-integration-card__banner-text {
  color: #344054;
}

.slack-integration-card__banner--error {
  border: 1px solid #d92d20;
  background: #fee4e2;
}

.slack-integration-card__banner--error .slack-integration-card__banner-text {
  color: #7a271a;
}

.slack-integration-card__banner-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.slack-integration-card__banner-text {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.slack-integration-card__actions {
  display: flex;
  gap: 12px;
}
</style>
